/**
 * SeriesChart — native SVG multi-dataset line/bar/area chart.
 *
 * Replaces the prior Chart.js-backed SeriesChart and the interim
 * MiniSeriesChart. No runtime Chart.js dependency.
 *
 * Highlights:
 *   - Multiple datasets on a shared X axis (line, bar, area).
 *   - Bar charts default to STACKED — pass `stacked: false` (or `grouped: true`)
 *     to opt out. `stacked: 'auto'` (default) → bar stacks, line/area don't.
 *   - Built-in palette + golden-angle HSL fallback. Per-series `color`
 *     and per-chart `colors` / `colorGenerator` always win.
 *   - Click-to-toggle legend, hover-isolated highlighting, animated `setData`.
 *   - Accepts three data shapes:
 *       1. { labels, datasets: [{ label, data, color?, fill?, smoothing? }] }
 *       2. { labels, series:   [{ name,  data, color?, fill?, smoothing? }] }
 *       3. [v0, v1, v2, ...]  (single-series shorthand)
 */

import View from '@core/View.js';
import dataFormatter from '@core/utils/DataFormatter.js';

const SERIES_COLORS = [
    '#36A2EB', '#FF6384', '#FFCE56', '#4BC0C0', '#9966FF',
    '#FF9F40', '#66BB6A', '#EF5350', '#AB47BC', '#26C6DA'
];

// Golden angle in degrees, gives visually distinct hues for any N.
const GOLDEN_ANGLE = 137.508;

const defaultColorGenerator = (i) =>
    `hsl(${((i * GOLDEN_ANGLE) % 360).toFixed(0)}, 65%, 52%)`;

class SeriesChart extends View {
    constructor(options = {}) {
        super({
            className: 'mini-series-chart',
            ...options
        });

        // Chart type
        this.chartType = options.chartType || 'line';

        // Stacking — 'auto' resolves to true for bar, false otherwise.
        // grouped:true is documented alias for stacked:false on bar charts.
        if (options.grouped === true) {
            this.stacked = false;
        } else if (options.stacked === undefined) {
            this.stacked = 'auto';
        } else {
            this.stacked = options.stacked;
        }

        // Dimensions
        this.width = options.width || '100%';
        this.height = options.height || 200;

        // Axis label visibility — gridlines (showGrid) stay independent.
        // padBottom/padLeft shrink to a small breathing margin when the
        // corresponding label set is hidden so the plot reclaims the space.
        this.showXLabels = options.showXLabels !== false;
        this.showYLabels = options.showYLabels !== false;

        this.padTop = 10;
        this.padRight = 12;
        this.padBottom = this.showXLabels ? 24 : 8;
        this.padLeft = this.showYLabels ? 40 : 8;
        // Per-paint override (set by _buildGeometry when X-labels need extra
        // room for rotation). Resets every paint; null falls back to padBottom.
        this._padBottomOverride = null;

        // Line / area
        this.strokeWidth = options.strokeWidth || 2;
        this.smoothing = options.smoothing ?? 0.3;
        this.showDots = options.showDots !== false;
        this.dotRadius = options.dotRadius || 3;
        this.fill = options.fill ?? (this.chartType === 'area');

        // Bar
        this.barGap = options.barGap ?? 2;
        this.groupGap = options.groupGap ?? 6;

        // Grid
        this.showGrid = options.showGrid !== false;
        this.gridLines = options.gridLines || 5;

        // Legend
        this.showLegend = options.showLegend !== false;
        this.legendPosition = options.legendPosition || 'top';
        this.legendJustify = this._normalizeLegendJustify(options.legendJustify);

        // Tooltip
        this.showTooltip = options.showTooltip !== false;

        // Hover-highlight: dim all other series when the cursor enters a
        // bar or dot so the active series pops. Off by default — pass
        // `highlightOnHover: true` to re-enable. Was on by default in
        // earlier releases; consumers found the dim flicker distracting on
        // stacked-bar charts in particular.
        this.highlightOnHover = options.highlightOnHover === true;

        // Colors
        this.colors = Array.isArray(options.colors) && options.colors.length
            ? [...options.colors]
            : [...SERIES_COLORS];
        this.colorGenerator = options.colorGenerator || defaultColorGenerator;

        // Formatting
        this.valueFormatter = options.valueFormatter || null;
        this.xLabelFormat = options.xLabelFormat || null;
        this.xLabelFormatter = options.xLabelFormatter || null;
        this.dataFormatter = dataFormatter;

        // Animation
        this.animate = options.animate !== false;
        this.animationDuration = options.animationDuration || 300;

        // Crosshair tracking (Chart.js-style "mode: index" tooltip on line/area)
        // Off by default. When on AND chartType !== 'bar', a transparent rect
        // overlays the plot area; mousemove snaps to the nearest column and
        // shows a crosshair + per-dataset ghost dot + multi-row tooltip.
        this.crosshairTracking = options.crosshairTracking === true;
        // null → use the CSS default (Bootstrap-aware via currentColor).
        // A non-null value sets `stroke` directly on the line element.
        this.crosshairColor = options.crosshairColor || null;
        this.crosshairWidth = options.crosshairWidth || 1;

        // Data
        this._rawData = options.data || null;
        this._labels = [];
        this._datasets = [];
        this._hidden = new Set(); // indices of hidden datasets

        // Render bookkeeping
        this._tweenId = 0;
        this._currentGeometry = null; // last rendered geometry, used as tween source

        // Crosshair-tracking DOM cache (rebuilt on each paint).
        this._crosshairLayer = null;
        this._crosshairLine = null;
        this._ghostDots = null;
        this._hitRect = null;
    }

    // ── template ──────────────────────────────────────────────────────

    getTemplate() {
        const widthStyle = typeof this.width === 'number' ? `${this.width}px` : this.width;
        const heightStyle = typeof this.height === 'number' ? `${this.height}px` : this.height;

        return `
            <div class="mini-series-wrapper mini-series-legend-${this.legendPosition} mini-series-legend-justify-${this.legendJustify}">
                ${this.showLegend ? '<div class="mini-series-legend"></div>' : ''}
                <div class="mini-series-svg-area" style="width:${widthStyle}; height:${heightStyle};">
                    <svg class="mini-series-svg" width="100%" height="100%"
                         preserveAspectRatio="none" style="display:block;">
                    </svg>
                    ${this.showTooltip ? '<div class="mini-series-tooltip" style="display:none;"></div>' : ''}
                </div>
            </div>
        `;
    }

    // ── lifecycle ─────────────────────────────────────────────────────

    async onAfterRender() {
        this.svg = this.element.querySelector('.mini-series-svg');
        this.tooltipEl = this.element.querySelector('.mini-series-tooltip');
        this.legendEl = this.element.querySelector('.mini-series-legend');

        this._updateDimensions();
        this._setupResizeObserver();

        if (this._rawData) {
            this._parseData(this._rawData);
            this._renderChart({ animate: false });
            this._renderLegend();
        }
    }

    async onBeforeDestroy() {
        if (this._resizeObserver) {
            this._resizeObserver.disconnect();
            this._resizeObserver = null;
        }
        this._tweenId++;
        await super.onBeforeDestroy();
    }

    // ── public API ────────────────────────────────────────────────────

    setData(data, options = {}) {
        this._rawData = data;
        this._parseData(data);
        if (this.svg) {
            const animate = options.animate ?? this.animate;
            this._renderChart({ animate });
            this._renderLegend();
        }
    }

    setChartType(newType) {
        if (newType === this.chartType) return;
        this.chartType = newType;
        if (this.fill === undefined) this.fill = (newType === 'area');
        if (this.svg) {
            this._renderChart({ animate: false });
            this._renderLegend();
        }
    }

    toggleSeries(index) {
        if (this._hidden.has(index)) this._hidden.delete(index);
        else this._hidden.add(index);
        if (this.svg) {
            this._renderChart({ animate: this.animate });
            this._renderLegend();
        }
        this.emit?.('chart:series-toggled', {
            chart: this, index, hidden: this._hidden.has(index)
        });
    }

    // ── dimensions ────────────────────────────────────────────────────

    _updateDimensions() {
        if (!this.svg) return;
        const rect = this.svg.getBoundingClientRect();
        this._w = rect.width || 300;
        this._h = rect.height || (typeof this.height === 'number' ? this.height : 200);
        this.svg.setAttribute('viewBox', `0 0 ${this._w} ${this._h}`);
    }

    _setupResizeObserver() {
        if (typeof ResizeObserver === 'undefined') return;
        this._resizeObserver = new ResizeObserver(() => {
            this._updateDimensions();
            if (this._datasets.length > 0) this._renderChart({ animate: false });
        });
        if (this.svg) this._resizeObserver.observe(this.svg);
    }

    // ── data normalisation ────────────────────────────────────────────

    _parseData(data) {
        if (!data) { this._labels = []; this._datasets = []; return; }

        let labels = [];
        let datasets = [];

        if (Array.isArray(data)) {
            // [v0, v1, ...] single-series shorthand
            labels = data.map((_, i) => String(i + 1));
            datasets = [{ label: 'Series 1', data: [...data] }];
        } else if (data.labels && data.datasets) {
            labels = data.labels;
            datasets = data.datasets.map((ds, i) => ({
                label: ds.label || `Series ${i + 1}`,
                data: ds.data || [],
                color: ds.color || ds.borderColor,
                fill: ds.fill,
                smoothing: ds.smoothing,
                fillColor: ds.backgroundColor || ds.fillColor
            }));
        } else if (data.labels && data.series) {
            labels = data.labels;
            datasets = data.series.map((s, i) => ({
                label: s.name || s.label || `Series ${i + 1}`,
                data: s.data || [],
                color: s.color || s.borderColor,
                fill: s.fill,
                smoothing: s.smoothing,
                fillColor: s.backgroundColor || s.fillColor
            }));
        }

        // Resolve color and fill once, here.
        this._labels = labels;
        this._datasets = datasets.map((ds, i) => {
            const color = ds.color || this.colors[i] || this.colorGenerator(i);
            const fill = ds.fill ?? (this.chartType === 'area' ? true : (this.chartType === 'bar' ? true : this.fill));
            const fillColor = ds.fillColor || this._toRgba(color, this.chartType === 'bar' ? 0.85 : 0.18);
            return {
                label: ds.label,
                data: ds.data,
                color,
                fillColor,
                fill,
                smoothing: ds.smoothing ?? this.smoothing
            };
        });

        // Trim hidden indices that no longer exist.
        if (this._hidden.size) {
            this._hidden = new Set([...this._hidden].filter(i => i < this._datasets.length));
        }
    }

    _isStacked() {
        if (this.stacked === 'auto') return this.chartType === 'bar';
        return !!this.stacked;
    }

    _visibleDatasets() {
        return this._datasets
            .map((ds, i) => ({ ds, i }))
            .filter(({ i }) => !this._hidden.has(i));
    }

    // ── chart area helpers ────────────────────────────────────────────

    get _plotLeft() { return this.padLeft; }
    get _plotTop() { return this.padTop; }
    get _plotRight() { return this._w - this.padRight; }
    get _plotBottom() { return this._h - (this._padBottomOverride ?? this.padBottom); }
    get _plotW() { return this._plotRight - this._plotLeft; }
    get _plotH() { return this._plotBottom - this._plotTop; }

    _calcBounds() {
        let min = Infinity, max = -Infinity;
        const visible = this._visibleDatasets();

        if (this._isStacked() && this.chartType === 'bar') {
            const count = this._labels.length || (visible[0]?.ds.data.length || 0);
            for (let i = 0; i < count; i++) {
                let pos = 0, neg = 0;
                for (const { ds } of visible) {
                    const v = +ds.data[i] || 0;
                    if (v >= 0) pos += v; else neg += v;
                }
                if (pos > max) max = pos;
                if (neg < min) min = neg;
            }
            if (!isFinite(min)) min = 0;
            if (!isFinite(max)) max = 1;
        } else {
            for (const { ds } of visible) {
                for (const v of ds.data) {
                    if (v < min) min = v;
                    if (v > max) max = v;
                }
            }
        }

        if (!isFinite(min)) { min = 0; max = 1; }
        if (min === max) { min -= 1; max += 1; }
        const range = max - min;
        max += range * 0.05;
        if (min > 0) min = Math.max(0, min - range * 0.05);
        return { min, max };
    }

    _yToPixel(value, min, max) {
        return this._plotBottom - ((value - min) / (max - min)) * this._plotH;
    }

    _xToPixel(index, count) {
        if (count <= 1) return this._plotLeft + this._plotW / 2;
        return this._plotLeft + (index / (count - 1)) * this._plotW;
    }

    // ── render ────────────────────────────────────────────────────────

    _renderChart({ animate = false } = {}) {
        if (!this.svg) return;
        this._updateDimensions();

        // Cancel any in-flight tween.
        this._tweenId++;
        const tweenId = this._tweenId;

        // Hide the crosshair before rebuilding so it doesn't paint at a stale
        // column while the next geometry is being computed.
        if (this.crosshairTracking) this._clearCrosshair();

        if (this._datasets.length === 0) {
            this.svg.innerHTML = '';
            this._currentGeometry = null;
            return;
        }

        const { min, max } = this._calcBounds();
        const count = this._labels.length || (this._datasets[0]?.data.length || 0);

        const targetGeometry = this._buildGeometry(min, max, count);

        const previous = this._currentGeometry;
        const canTween = animate && previous
            && previous.chartType === targetGeometry.chartType
            && this.animationDuration > 0
            && typeof requestAnimationFrame !== 'undefined';

        if (!canTween) {
            this._paintFrame(targetGeometry);
            this._currentGeometry = targetGeometry;
            return;
        }

        // Tween from previous geometry → target geometry.
        const start = performance.now();
        const duration = this.animationDuration;
        const ease = (t) => 1 - Math.pow(1 - t, 3); // easeOutCubic

        const step = (now) => {
            if (this._tweenId !== tweenId) return; // cancelled
            const t = Math.min(1, (now - start) / duration);
            const k = ease(t);
            const interp = this._interpolateGeometry(previous, targetGeometry, k);
            this._paintFrame(interp);
            if (t < 1) requestAnimationFrame(step);
            else this._currentGeometry = targetGeometry;
        };
        requestAnimationFrame(step);
    }

    _buildGeometry(min, max, count) {
        // Reset per-paint pad override; pre-flight may bump it for rotation.
        this._padBottomOverride = null;

        // Snap (min, max) to "nice" tick boundaries so Y labels read as
        // 0/25/50/75/100 instead of 0/28.77/57.54/...
        const ticks = this._niceTicks(min, max, this.gridLines);
        min = ticks.niceMin;
        max = ticks.niceMax;
        const tickStep = ticks.step;
        const tickCount = ticks.count;

        // Pre-flight: format X labels and decide whether they need rotation.
        // Slot width depends on chart type; line/area labels sit AT data
        // points so the slot is between them. Skipped entirely when X labels
        // are hidden — _padBottomOverride stays null and padBottom (already
        // shrunk to 8 in the constructor) reclaims the space.
        const formattedX = [];
        let xLabelsRotated = false;
        if (this.showXLabels) {
            const maxXLabels = Math.max(2, Math.floor(this._plotW / 60));
            const stepX = Math.max(1, Math.ceil(count / maxXLabels));
            for (let i = 0; i < count; i += stepX) {
                const raw = this._labels[i] !== undefined ? this._labels[i] : '';
                formattedX.push({ index: i, text: this._truncateLabel(this._formatXLabel(raw)) });
            }
            const slotW = (this.chartType === 'bar')
                ? this._plotW / Math.max(1, count)
                : this._plotW / Math.max(1, count - 1);
            const widest = formattedX.reduce((m, l) => Math.max(m, l.text.length * 6.5), 0);
            xLabelsRotated = widest > slotW * 0.9;
            if (xLabelsRotated) this._padBottomOverride = 48;
        }

        // baseline / plot dims now reflect the (possibly bumped) padBottom.
        const baseline = this._yToPixel(Math.max(min, Math.min(0, max)), min, max);
        const visible = this._visibleDatasets();

        const geom = {
            chartType: this.chartType,
            min, max, count, baseline,
            xLabelsRotated,
            grid: [],
            yLabels: [],
            xLabels: [],
            lines: [],
            bars: []
        };

        // Grid + Y-axis labels — anchored to nice tick values. Grid lines
        // always populate (gated separately by showGrid in _paintFrame);
        // Y-label generation skips when showYLabels is false.
        for (let i = 0; i < tickCount; i++) {
            const value = min + i * tickStep;
            const y = this._yToPixel(value, min, max);
            geom.grid.push({ x1: this._plotLeft, y1: y, x2: this._plotRight, y2: y });
            if (this.showYLabels) {
                geom.yLabels.push({
                    x: this._plotLeft - 4,
                    y: y + 3,
                    text: this._formatAxisValue(value, tickStep)
                });
            }
        }

        // X-axis labels. formattedX is empty when showXLabels is false, so
        // this loop is a no-op in that case.
        for (const { index, text } of formattedX) {
            const x = (this.chartType === 'bar')
                ? this._barSlotCenter(index, count)
                : this._xToPixel(index, count);
            geom.xLabels.push({ x, y: this._plotBottom + 14, text });
        }

        if (this.chartType === 'bar') {
            this._buildBars(geom, visible, min, max, count);
        } else {
            this._buildLines(geom, visible, min, max, count);
        }

        return geom;
    }

    _barSlotCenter(i, count) {
        if (count <= 0) return this._plotLeft;
        const slotW = this._plotW / count;
        return this._plotLeft + i * slotW + slotW / 2;
    }

    _buildBars(geom, visible, min, max, count) {
        const stacked = this._isStacked();
        const slotW = this._plotW / Math.max(count, 1);
        const groupW = Math.max(2, slotW - this.groupGap);

        if (stacked) {
            const barW = groupW;
            for (let i = 0; i < count; i++) {
                let posCum = 0, negCum = 0;
                for (const { ds, i: dsIdx } of visible) {
                    const v = +ds.data[i] || 0;
                    if (v === 0) continue;
                    let yTop, yBottom;
                    if (v >= 0) {
                        yTop = this._yToPixel(posCum + v, min, max);
                        yBottom = this._yToPixel(posCum, min, max);
                        posCum += v;
                    } else {
                        yTop = this._yToPixel(negCum, min, max);
                        yBottom = this._yToPixel(negCum + v, min, max);
                        negCum += v;
                    }
                    const x = this._plotLeft + i * slotW + (slotW - barW) / 2;
                    geom.bars.push({
                        x, y: Math.min(yTop, yBottom),
                        w: barW,
                        h: Math.max(0.5, Math.abs(yBottom - yTop)),
                        color: ds.color, fillColor: ds.fillColor,
                        dsIdx, dataIdx: i, value: v, label: this._labels[i]
                    });
                }
            }
        } else {
            const numSeries = visible.length || 1;
            const barW = Math.max(1, (groupW - (numSeries - 1) * this.barGap) / numSeries);
            for (let s = 0; s < numSeries; s++) {
                const { ds, i: dsIdx } = visible[s];
                for (let i = 0; i < count; i++) {
                    const v = +ds.data[i] || 0;
                    const slotX = this._plotLeft + i * slotW + (slotW - groupW) / 2;
                    const x = slotX + s * (barW + this.barGap);
                    const y = this._yToPixel(v, min, max);
                    const h = Math.abs(geom.baseline - y);
                    geom.bars.push({
                        x, y: Math.min(y, geom.baseline),
                        w: barW, h: Math.max(h, 0.5),
                        color: ds.color, fillColor: ds.fillColor,
                        dsIdx, dataIdx: i, value: v, label: this._labels[i]
                    });
                }
            }
        }
    }

    _buildLines(geom, visible, min, max, count) {
        for (const { ds, i: dsIdx } of visible) {
            const points = ds.data.map((v, i) => ({
                x: this._xToPixel(i, count),
                y: this._yToPixel(+v || 0, min, max),
                value: +v || 0,
                index: i,
                label: this._labels[i]
            }));
            geom.lines.push({
                dsIdx,
                color: ds.color,
                fillColor: ds.fillColor,
                fill: ds.fill,
                smoothing: ds.smoothing ?? this.smoothing,
                points
            });
        }
    }

    _paintFrame(geom) {
        if (!this.svg) return;
        this.svg.innerHTML = '';

        // Grid
        if (this.showGrid) {
            for (const g of geom.grid) {
                this.svg.appendChild(this._svgEl('line', {
                    x1: g.x1, y1: g.y1, x2: g.x2, y2: g.y2,
                    stroke: 'var(--bs-border-color, #dee2e6)',
                    'stroke-width': 0.5,
                    'stroke-dasharray': '3,3'
                }));
            }
        }

        // Y-axis labels
        if (this.showYLabels) {
            for (const l of geom.yLabels) {
                const t = this._svgEl('text', {
                    x: l.x, y: l.y, 'text-anchor': 'end',
                    'font-size': '10', fill: 'var(--bs-secondary-color, #6c757d)'
                });
                t.textContent = l.text;
                this.svg.appendChild(t);
            }
        }

        // X-axis labels — rotate -45° when they would overlap their slots.
        if (this.showXLabels) {
            for (const l of geom.xLabels) {
                const attrs = {
                    x: l.x, y: l.y,
                    'font-size': '10',
                    fill: 'var(--bs-secondary-color, #6c757d)'
                };
                if (geom.xLabelsRotated) {
                    attrs['text-anchor'] = 'end';
                    attrs.transform = `rotate(-45 ${l.x} ${l.y})`;
                } else {
                    attrs['text-anchor'] = 'middle';
                }
                const t = this._svgEl('text', attrs);
                t.textContent = l.text;
                this.svg.appendChild(t);
            }
        }

        if (geom.chartType === 'bar') {
            this._paintBars(geom);
        } else {
            this._paintLines(geom);
        }

        this._attachHoverListeners();

        // Crosshair-tracking overlay (line/area only). Mounted last so the
        // hit-rect captures hover above all marks. Does nothing when off.
        if (this.crosshairTracking
            && geom.chartType !== 'bar'
            && geom.lines.length > 0) {
            this._setupCrosshairTracking(geom);
        } else {
            // Drop stale references in case the mode was just turned off.
            this._crosshairLayer = null;
            this._crosshairLine = null;
            this._ghostDots = null;
            this._hitRect = null;
        }
    }

    _paintLines(geom) {
        for (const line of geom.lines) {
            // Area fill
            if (line.fill && line.points.length > 1) {
                const path = this._buildLinePath(line.points, line.smoothing)
                    + ` L ${line.points[line.points.length - 1].x},${this._plotBottom}`
                    + ` L ${line.points[0].x},${this._plotBottom} Z`;
                this.svg.appendChild(this._svgEl('path', {
                    d: path, fill: line.fillColor, stroke: 'none',
                    class: `mini-series-area mini-series-ds-${line.dsIdx}`
                }));
            }
            // Line
            if (line.points.length > 1) {
                this.svg.appendChild(this._svgEl('path', {
                    d: this._buildLinePath(line.points, line.smoothing),
                    fill: 'none',
                    stroke: line.color,
                    'stroke-width': this.strokeWidth,
                    'stroke-linecap': 'round',
                    'stroke-linejoin': 'round',
                    class: `mini-series-line mini-series-ds-${line.dsIdx}`
                }));
            }
            // Dots
            if (this.showDots) {
                for (const p of line.points) {
                    this.svg.appendChild(this._svgEl('circle', {
                        cx: p.x, cy: p.y, r: this.dotRadius,
                        fill: line.color,
                        class: `mini-series-dot mini-series-ds-${line.dsIdx}`,
                        'data-ds': line.dsIdx,
                        'data-col': p.index
                    }));
                }
            }
        }
    }

    _paintBars(geom) {
        for (const b of geom.bars) {
            this.svg.appendChild(this._svgEl('rect', {
                x: b.x, y: b.y, width: b.w, height: b.h,
                fill: b.fillColor || b.color,
                rx: 1,
                class: `mini-series-bar mini-series-ds-${b.dsIdx}`,
                'data-ds': b.dsIdx,
                'data-col': b.dataIdx
            }));
        }
    }

    // ── crosshair tracking (line/area only) ──────────────────────────

    _setupCrosshairTracking(geom) {
        // Build a single <g> containing the vertical line, one ghost dot per
        // visible dataset, and a transparent hit-rect that captures hover.
        const layer = this._svgEl('g', { class: 'mini-series-crosshair-layer' });

        const lineAttrs = {
            x1: 0, y1: this._plotTop, x2: 0, y2: this._plotBottom,
            'stroke-width': this.crosshairWidth,
            class: 'mini-series-crosshair',
            style: 'display:none;'
        };
        if (this.crosshairColor) {
            lineAttrs.stroke = this.crosshairColor;
        } else {
            lineAttrs.stroke = 'currentColor';
        }
        const xLine = this._svgEl('line', lineAttrs);
        layer.appendChild(xLine);

        // One ghost dot per visible dataset, in the same order as geom.lines.
        const ghosts = geom.lines.map((line) => {
            const dot = this._svgEl('circle', {
                cx: 0, cy: 0, r: this.dotRadius + 1,
                fill: line.color,
                class: `mini-series-ghost mini-series-ds-${line.dsIdx}`,
                'data-ds': line.dsIdx,
                style: 'display:none;'
            });
            layer.appendChild(dot);
            return dot;
        });

        // Hit-rect — last child of the layer so it's above the ghosts.
        const hit = this._svgEl('rect', {
            x: this._plotLeft, y: this._plotTop,
            width: this._plotW, height: this._plotH,
            class: 'mini-series-hit'
        });
        layer.appendChild(hit);

        this.svg.appendChild(layer);

        this._crosshairLayer = layer;
        this._crosshairLine = xLine;
        this._ghostDots = ghosts;
        this._hitRect = hit;

        // Listeners
        hit.addEventListener('mousemove', (event) => {
            const rect = this.svg.getBoundingClientRect();
            const cursorX = event.clientX - rect.left;
            const colIdx = this._findColumn(cursorX);
            if (colIdx < 0 || !this._currentGeometry) {
                this._clearCrosshair();
                this._hideTooltip();
                return;
            }
            this._paintCrosshair(colIdx, this._currentGeometry);
            if (this.showTooltip) this._showTooltip(colIdx, event);
        });
        hit.addEventListener('mouseleave', () => {
            this._clearCrosshair();
            this._hideTooltip();
            this._clearFade();
        });
        hit.addEventListener('click', (event) => {
            const rect = this.svg.getBoundingClientRect();
            const cursorX = event.clientX - rect.left;
            const colIdx = this._findColumn(cursorX);
            if (colIdx < 0) return;
            // Match Chart.js mode:'index' — emit the column for the first
            // visible dataset. Per-dataset clicks remain available with
            // crosshairTracking: false.
            const firstVisible = this._visibleDatasets()[0];
            if (!firstVisible) return;
            this.emit?.('chart:click', {
                chart: this,
                datasetIndex: firstVisible.i,
                index: colIdx,
                value: firstVisible.ds.data[colIdx],
                label: this._labels[colIdx]
            });
        });
    }

    _findColumn(cursorX) {
        const count = this._labels.length
            || (this._datasets[0]?.data.length || 0);
        if (count < 2) return -1;
        if (cursorX < this._plotLeft || cursorX > this._plotRight) return -1;
        const ratio = (cursorX - this._plotLeft) / this._plotW;
        const idx = Math.round(ratio * (count - 1));
        if (idx < 0) return 0;
        if (idx > count - 1) return count - 1;
        return idx;
    }

    _paintCrosshair(colIdx, geom) {
        if (!this._crosshairLine || !geom || !geom.lines.length) return;

        // All visible lines share the same X grid — pick from the first.
        const firstLine = geom.lines[0];
        const point = firstLine.points[colIdx];
        if (!point) return;

        this._crosshairLine.setAttribute('x1', point.x);
        this._crosshairLine.setAttribute('x2', point.x);
        this._crosshairLine.style.display = '';

        // Place each ghost dot at its line's (x, y) for this column.
        for (let k = 0; k < geom.lines.length; k++) {
            const dot = this._ghostDots && this._ghostDots[k];
            if (!dot) continue;
            const p = geom.lines[k].points[colIdx];
            if (!p) {
                dot.style.display = 'none';
                continue;
            }
            dot.setAttribute('cx', p.x);
            dot.setAttribute('cy', p.y);
            dot.style.display = '';
        }
    }

    _clearCrosshair() {
        if (this._crosshairLine) this._crosshairLine.style.display = 'none';
        if (this._ghostDots) {
            for (const dot of this._ghostDots) dot.style.display = 'none';
        }
    }

    _buildLinePath(points, smoothing) {
        if (!points || points.length === 0) return '';
        if (smoothing > 0 && points.length >= 2) {
            let d = `M ${points[0].x},${points[0].y}`;
            for (let i = 0; i < points.length - 1; i++) {
                const cur = points[i], nxt = points[i + 1];
                const cp1x = cur.x + (nxt.x - cur.x) * smoothing;
                const cp2x = nxt.x - (nxt.x - cur.x) * smoothing;
                d += ` C ${cp1x},${cur.y} ${cp2x},${nxt.y} ${nxt.x},${nxt.y}`;
            }
            return d;
        }
        return points.map((p, i) => `${i === 0 ? 'M' : 'L'} ${p.x},${p.y}`).join(' ');
    }

    // ── tween helpers ─────────────────────────────────────────────────

    _interpolateGeometry(a, b, k) {
        // Use shape of `b` (target) as the canonical structure; tween numeric
        // fields from the matching entry in `a` where one exists.
        const lerp = (x, y) => x + (y - x) * k;
        const out = {
            chartType: b.chartType,
            min: b.min, max: b.max, count: b.count, baseline: b.baseline,
            grid: b.grid,
            yLabels: b.yLabels,
            xLabels: b.xLabels,
            lines: [],
            bars: []
        };

        if (b.chartType === 'bar') {
            out.bars = b.bars.map((tb) => {
                const sb = a.bars.find(s => s.dsIdx === tb.dsIdx && s.dataIdx === tb.dataIdx);
                if (!sb) {
                    // Animate in from baseline.
                    return { ...tb, y: lerp(a.baseline, tb.y), h: lerp(0, tb.h) };
                }
                return {
                    ...tb,
                    x: lerp(sb.x, tb.x),
                    y: lerp(sb.y, tb.y),
                    w: lerp(sb.w, tb.w),
                    h: lerp(sb.h, tb.h)
                };
            });
        } else {
            out.lines = b.lines.map((tl) => {
                const sl = a.lines.find(s => s.dsIdx === tl.dsIdx);
                const points = tl.points.map((tp, i) => {
                    const sp = sl && sl.points[i];
                    if (!sp) return { ...tp, y: lerp(a.baseline, tp.y) };
                    return { ...tp, x: lerp(sp.x, tp.x), y: lerp(sp.y, tp.y) };
                });
                return { ...tl, points };
            });
        }
        return out;
    }

    // ── tooltip / hover ───────────────────────────────────────────────

    _attachHoverListeners() {
        if (!this.svg) return;

        const els = this.svg.querySelectorAll('.mini-series-bar, .mini-series-dot');
        els.forEach(el => {
            const col = parseInt(el.getAttribute('data-col'), 10);
            const dsIdx = parseInt(el.getAttribute('data-ds'), 10);
            if (isNaN(col) || isNaN(dsIdx)) return;

            el.addEventListener('mouseenter', (e) => {
                if (this.showTooltip) this._showTooltip(col, e);
                if (this.highlightOnHover) this._fadeOtherSeries(dsIdx);
            });
            el.addEventListener('mousemove', (e) => {
                if (this.showTooltip) this._moveTooltip(e);
            });
            el.addEventListener('mouseleave', () => {
                this._hideTooltip();
                if (this.highlightOnHover) this._clearFade();
            });
            el.addEventListener('click', () => {
                const value = this._datasets[dsIdx]?.data[col];
                this.emit?.('chart:click', {
                    chart: this,
                    datasetIndex: dsIdx,
                    index: col,
                    value,
                    label: this._labels[col]
                });
            });
        });
    }

    _fadeOtherSeries(activeDsIdx) {
        if (!this.svg) return;
        const all = this.svg.querySelectorAll('[class*="mini-series-ds-"]');
        all.forEach(el => {
            const m = el.getAttribute('class').match(/mini-series-ds-(\d+)/);
            if (!m) return;
            const idx = parseInt(m[1], 10);
            if (idx !== activeDsIdx) el.classList.add('mini-series-faded');
            else el.classList.remove('mini-series-faded');
        });
    }

    _clearFade() {
        if (!this.svg) return;
        this.svg.querySelectorAll('.mini-series-faded')
            .forEach(el => el.classList.remove('mini-series-faded'));
    }

    _showTooltip(index, event) {
        if (!this.tooltipEl) return;
        const label = this._labels[index] !== undefined ? this._labels[index] : `#${index + 1}`;

        // Build via DOM nodes — colors come from API responses, so we set
        // `style.background` as a property (safe) rather than interpolating
        // into a `style="..."` attribute string.
        this.tooltipEl.innerHTML = '';
        const head = document.createElement('div');
        head.className = 'mini-series-tooltip-label';
        head.textContent = String(this._formatXLabel(label));
        this.tooltipEl.appendChild(head);

        for (let i = 0; i < this._datasets.length; i++) {
            if (this._hidden.has(i)) continue;
            const ds = this._datasets[i];
            const v = ds.data[index];

            const row = document.createElement('div');
            row.className = 'mini-series-tooltip-row';

            const swatch = document.createElement('span');
            swatch.className = 'mini-series-tooltip-swatch';
            swatch.style.background = ds.color;
            row.appendChild(swatch);

            const name = document.createElement('span');
            name.textContent = `${ds.label}:`;
            row.appendChild(name);

            const strong = document.createElement('strong');
            strong.textContent = this._formatValue(v);
            row.appendChild(strong);

            this.tooltipEl.appendChild(row);
        }

        this.tooltipEl.style.display = 'block';
        this._moveTooltip(event);
    }

    _moveTooltip(event) {
        if (!this.tooltipEl || this.tooltipEl.style.display === 'none') return;
        const area = this.element.querySelector('.mini-series-svg-area');
        if (!area) return;
        const areaRect = area.getBoundingClientRect();
        const x = event.clientX - areaRect.left;
        const y = event.clientY - areaRect.top;

        this.tooltipEl.style.left = '0px';
        this.tooltipEl.style.top = '0px';
        this.tooltipEl.style.transform = 'none';
        const tw = this.tooltipEl.offsetWidth;
        const th = this.tooltipEl.offsetHeight;
        const aw = areaRect.width;
        const ah = areaRect.height;

        let left = x - tw / 2;
        let top = y - th - 8;
        if (left < 0) left = 0;
        if (left + tw > aw) left = aw - tw;
        if (top < 0) top = y + 12;
        if (top + th > ah) top = ah - th;

        this.tooltipEl.style.left = `${left}px`;
        this.tooltipEl.style.top = `${top}px`;
    }

    _hideTooltip() {
        if (this.tooltipEl) this.tooltipEl.style.display = 'none';
    }

    // ── legend ────────────────────────────────────────────────────────

    _normalizeLegendJustify(value) {
        if (value === undefined) return 'start';
        if (value === 'start' || value === 'center' || value === 'end') return value;
        console.warn(`SeriesChart: invalid legendJustify "${value}" — expected 'start' | 'center' | 'end'. Falling back to 'start'.`);
        return 'start';
    }

    _renderLegend() {
        if (!this.showLegend || !this.legendEl) return;
        this.legendEl.innerHTML = '';

        this._datasets.forEach((ds, i) => {
            const item = document.createElement('span');
            item.className = 'mini-series-legend-item';
            if (this._hidden.has(i)) item.classList.add('mini-series-legend-hidden');
            item.setAttribute('role', 'button');
            item.setAttribute('data-ds', String(i));

            const swatch = document.createElement('span');
            swatch.className = 'mini-series-legend-swatch';
            swatch.style.background = ds.color;
            item.appendChild(swatch);

            const labelEl = document.createElement('span');
            labelEl.className = 'mini-series-legend-label';
            labelEl.textContent = ds.label;
            item.appendChild(labelEl);

            item.addEventListener('click', () => this.toggleSeries(i));
            this.legendEl.appendChild(item);
        });
    }

    // ── formatters ────────────────────────────────────────────────────

    _formatValue(value) {
        if (value === null || value === undefined) return '';
        if (this.valueFormatter) {
            if (typeof this.valueFormatter === 'function') return this.valueFormatter(value);
            return this.dataFormatter.pipe(value, this.valueFormatter);
        }
        if (typeof value === 'number') return value.toLocaleString();
        return String(value);
    }

    _formatXLabel(label) {
        if (label === null || label === undefined) return '';
        if (this.xLabelFormatter) return this.xLabelFormatter(label);
        if (this.xLabelFormat) {
            const formatted = this.dataFormatter.pipe(label, this.xLabelFormat);
            // Pre-formatted backend labels (e.g. "16:00") are not parseable as
            // timestamps and the formatter returns ''. Fall back to the raw
            // label rather than dropping it from the axis.
            if (formatted) return formatted;
        }
        return String(label);
    }

    // ── nice-number axis tick helpers ─────────────────────────────────
    // Heckbert "Nice Numbers for Graph Labels" (Graphics Gems, 1990).
    // Snaps a range to the nearest 1/2/5 × 10ⁿ value so axis labels read
    // as 0/25/50/75/100 instead of 0/28.77/57.54/...

    _niceNumber(range, round) {
        if (!isFinite(range) || range <= 0) return 1;
        const exponent = Math.floor(Math.log10(range));
        const fraction = range / Math.pow(10, exponent);
        let nice;
        if (round) {
            if (fraction < 1.5)      nice = 1;
            else if (fraction < 3)   nice = 2;
            else if (fraction < 7)   nice = 5;
            else                     nice = 10;
        } else {
            if (fraction <= 1)       nice = 1;
            else if (fraction <= 2)  nice = 2;
            else if (fraction <= 5)  nice = 5;
            else                     nice = 10;
        }
        return nice * Math.pow(10, exponent);
    }

    _niceTicks(min, max, target) {
        if (min === max) { min -= 1; max += 1; }
        const range = this._niceNumber(max - min, false);
        const step = this._niceNumber(range / Math.max(1, target - 1), true);
        const niceMin = Math.floor(min / step) * step;
        const niceMax = Math.ceil(max / step) * step;
        const count = Math.round((niceMax - niceMin) / step) + 1;
        return { niceMin, niceMax, step, count };
    }

    _formatAxisValue(value, step) {
        if (this.valueFormatter) {
            if (typeof this.valueFormatter === 'function') return this.valueFormatter(value);
            return this.dataFormatter.pipe(value, this.valueFormatter);
        }
        if (Math.abs(value) >= 1_000_000_000) return (value / 1_000_000_000).toFixed(1) + 'B';
        if (Math.abs(value) >= 1_000_000) return (value / 1_000_000).toFixed(1) + 'M';
        if (Math.abs(value) >= 1_000) return (value / 1_000).toFixed(1) + 'K';
        if (Number.isInteger(value)) return String(value);
        // Pick decimal precision from the tick step so a step of 0.001 doesn't
        // collapse to "0.0". Caller passes step for nice-tick contexts.
        const decimals = step && step > 0 && step < 1
            ? Math.max(0, -Math.floor(Math.log10(step)))
            : 1;
        return value.toFixed(decimals);
    }

    // Rotated labels need more headroom; truncation is a fallback for the
    // pathological case (UUIDs, multi-field labels).
    _truncateLabel(text) {
        const s = String(text);
        return s.length > 24 ? s.substring(0, 23) + '…' : s;
    }

    // ── color helpers ─────────────────────────────────────────────────

    _toRgba(color, alpha) {
        if (!color) return color;
        const s = String(color).trim();

        if (s.startsWith('#')) {
            let hex = s.slice(1);
            if (hex.length === 3) hex = hex.split('').map(c => c + c).join('');
            if (hex.length === 6) {
                const r = parseInt(hex.slice(0, 2), 16);
                const g = parseInt(hex.slice(2, 4), 16);
                const b = parseInt(hex.slice(4, 6), 16);
                return `rgba(${r},${g},${b},${alpha})`;
            }
            return s;
        }

        let m = s.match(/^rgba?\(([^)]+)\)$/i);
        if (m) {
            const parts = m[1].split(',').map(p => p.trim());
            return `rgba(${parts[0]},${parts[1]},${parts[2]},${alpha})`;
        }

        m = s.match(/^hsla?\(([^)]+)\)$/i);
        if (m) {
            const parts = m[1].split(',').map(p => p.trim());
            return `hsla(${parts[0]},${parts[1]},${parts[2]},${alpha})`;
        }

        // Named or unknown — return as-is. Fill won't get alpha; documented.
        return s;
    }

    // ── DOM helpers ───────────────────────────────────────────────────

    _svgEl(tag, attrs = {}) {
        const el = document.createElementNS('http://www.w3.org/2000/svg', tag);
        for (const [k, v] of Object.entries(attrs)) {
            el.setAttribute(k, String(v));
        }
        return el;
    }

    _esc(text) {
        const d = document.createElement('div');
        d.textContent = text;
        return d.innerHTML;
    }
}

export default SeriesChart;
